import React, { ReactNode } from 'react'
import { Stack, Typography } from '@mui/material'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { ClientPrice } from '../ClientPrice.component'

export interface PricedCheckboxRowProps {
  checked: boolean
  onChange: (checked: boolean) => void
  title: ReactNode
  /** Shown after the title, e.g. an advisory icon. */
  titleAdornment?: ReactNode
  description?: ReactNode
  /** What ticking the row adds, shown as "+ price" on the right. */
  price?: { unitPrice: string, currency: string }
  /** Shown under the description, e.g. a quantity for a selected extra. */
  children?: ReactNode
  /** False when the price is the row's own rather than an addition to something else. Defaults to true. */
  showPlus?: boolean
}

/**
 * One tickable line of the product drawer: a checkbox labelled with a title and description, and what it
 * adds to the price.
 *
 * @example
 * <PricedCheckboxRow checked={on} onChange={toggle} title="Blurring" price={{ unitPrice: '9.00', currency: 'EUR' }} />
 */
export const PricedCheckboxRow: React.FC<PricedCheckboxRowProps> = ({
  checked,
  onChange,
  title,
  titleAdornment,
  description,
  price,
  children,
  showPlus = true,
}) => (
  <Stack
    direction="row"
    alignItems="flex-start"
    gap={1.2}
    sx={{ '& .MuiFormControlLabel-root': { alignItems: 'flex-start', flex: 1, minWidth: 0 } }}
  >

    <MUICheckbox
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      label={
        <Stack gap={0.4} sx={{ flex: 1, minWidth: 0 }}>

          <Stack direction="row" alignItems="center" gap={0.4}>
            <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>{title}</Typography>
            {titleAdornment}
          </Stack>

          {description &&
            <Typography variant="text-xs" color={GRAY_700} sx={{ lineHeight: 1.5 }}>{description}</Typography>
          }

          {children}

        </Stack>
      }
    />

    {price &&
      <Typography variant="text-sm" fontWeight={500} color={GRAY_700} sx={{ whiteSpace: 'nowrap', mt: 0.2 }}>
        <Stack direction="row" component="span" gap={0.4}>
          {showPlus ? '+ ' : ''}<ClientPrice unitPrice={price.unitPrice} currency={price.currency} variant="text-sm" />
        </Stack>
      </Typography>
    }

  </Stack>
)
